import { debounce } from "lodash";
import * as R from "ramda";
import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { deleteData, writeData } from "../api";
import {
  addPage,
  getPage,
  getPageBlocks,
  getPageIsPublished,
  getPages,
  moveBlockIndex,
  movePage,
  setPageIsPublished,
  setPageName,
} from "../api/page";
import { deleteTable } from "../api/table";
import { useAppId } from "../hook";
import { ActionType, AppDispatch, RootState } from "../reducer";
import { loadKey, loadStatus } from "./loadStatus";
import { useTrackedLoad } from "./loadTracking";
import { useLoadError } from "../slices/ui";
import {
  selectNotebookPages,
  selectNoteEdits,
  selectNotebook,
  selectSelectedNotebookPageId,
  makeSelectNotebookPage,
  makeSelectPageBlocks,
  makeSelectDatabasePages,
  selectNotebookPageIdMap,
} from "../selectors/page";
import {
  flushNoteEdits as flushNoteEditsUtil,
  selectedPage,
} from "../reducer/note/util";
import {
  FlushNoteEditsAction,
  MoveNoteBlockAction,
  RemoveNotebookPagesAction,
  SetBlockToFocusAction,
  SetDeleteTablesAction,
  SetNoteEditAction,
  SetPageBlocksAction,
  SetPageNameAction,
  SetPagePublishedAction,
  SetSelectedNotebookPageIdAction,
} from "../reducer/page";
import { Notebook, NotebookPage } from "../types/note";
import { NoteBlock } from "../types/notebook/note/block";
import { HtmlBlock, NoteEdits } from "../types/notebook/note/block/html";
import { PageBlock } from "../types/notebook/note/block/page";
import { useAppIdMap, useUserHasApp } from "./app";
import { useInsertBlock } from "./page/block";
import { useSubscriptionStatus } from "./settings";

export const useSelectMainNote: () => (config?: {
  notebook?: Notebook;
}) => void = () => {
  const [notebookState, setNotebook] = useNotebook();
  const [selectedNotebookPageId, setSelectedNotebookPageId] =
    useSelectedNotebookPageId();

  return (config = {}) => {
    const { notebook: notebookParam } = config;
    const notebook = (notebookParam ?? notebookState)!;
    const pageIds = notebook.pages.map(R.prop("page-id"));
    const pageId = pageIds.length !== 0 ? pageIds[0] : undefined;
    setSelectedNotebookPageId(pageId);
    // setSelectedNotebookPageId("b1be2ab0-d634-4aaf-a8ba-b4ae512dfad3");
  };
};

export const useAddNotebookPagesToState: () => (
  notebookPages: NotebookPage[],
) => void = () => {
  const dispatch = useDispatch<AppDispatch>();

  return (notebookPages) => {
    dispatch({ type: ActionType.ADD_NOTEBOOK_PAGES, notebookPages });
  };
};

export const useRemoveNotebookPagesFromState: () => (
  notebookPages: NotebookPage[],
) => void = () => {
  const dispatch = useDispatch<AppDispatch>();

  return (notebookPages) => {
    const action: RemoveNotebookPagesAction = {
      type: ActionType.REMOVE_NOTEBOOK_PAGES,
      notebookPages,
    };
    dispatch(action as any);
  };
};

export const useAddNotebookPage: () => (config: {
  folderId?: string;
  name: string;
  isDatabase?: boolean;
}) => Promise<any> = () => {
  const addPages = useAddNotebookPagesToState();
  const [appId] = useAppId();

  return ({ folderId, name, isDatabase }) => {
    if (!appId) {
      throw new Error("AppId undefined");
    }

    return addPage({ appId, folderId, name, isDatabase }).then((page) => {
      addPages([page]);
    });
  };
};

export const newNotebookPage: (config: {
  name: string;
  folderId: string;
  appId: string;
  isDatabase?: boolean;
}) => NotebookPage = ({ name, folderId, appId, isDatabase = false }) => {
  const isDatabaseKeys = isDatabase ? { "is-database": "true" } : {};
  return {
    "folder-id": folderId,
    "app-id": appId,
    "page-id": uuidv4(),
    "page-data": { name: name, ...isDatabaseKeys },
    type: "omega/query-omega/page/Page",
  };
};

export const useAddNotebookPageAsync: () => (
  page: NotebookPage,
) => NotebookPage = () => {
  const addNotebookPages = useAddNotebookPagesToState();

  return (page) => {
    writeData(page);
    addNotebookPages([page]);
    return page;
  };
};

export const useDeleteNotebookPage: () => (
  page: NotebookPage,
) => Promise<any> = () => {
  const removeNotebookPagesFromState = useRemoveNotebookPagesFromState();

  return async (page) => {
    removeNotebookPagesFromState([page]);
    return deleteData(page);
  };
};

export const useGetNotebookPage: () => (
  pageId?: string,
) => NotebookPage | undefined = () => {
  const notebookPages = useSelector(selectNotebookPages);

  return (pageId) => {
    return pageId && pageId in notebookPages
      ? notebookPages[pageId]
      : undefined;
  };
};

export const useNotebookPage = (pageId: string | undefined) => {
  const selectNotebookPage = useMemo(
    () => makeSelectNotebookPage(pageId),
    [pageId],
  );
  const page = useSelector(selectNotebookPage);
  return { page };
};

export const useNotebook: () => [
  Notebook | undefined,
  (notebook: Notebook) => void,
] = () => {
  const notebook = useSelector(selectNotebook);
  const dispatch = useDispatch<AppDispatch>();
  const setNotebook = (notebook: Notebook) => {
    dispatch({ type: ActionType.SET_NOTEBOOK, notebook });
  };
  return [notebook, setNotebook];
};

export const useSelectedNotebookPage: () => NotebookPage | undefined = () => {
  const [selectedNotebookPageId] = useSelectedNotebookPageId();
  const selector = useMemo(
    () => makeSelectNotebookPage(selectedNotebookPageId),
    [selectedNotebookPageId],
  );
  return useSelector(selector);
};

export const useNotebookPagePath: () => (config: {
  pageId?: string;
}) => string = () => {
  const [appId] = useAppId();
  return ({ pageId }) => {
    return `/app/${appId}/page/${pageId}`;
  };
};

export const useSelectedNotebookPageId: () => [
  string | undefined,
  (notebookPage?: string) => void,
] = () => {
  const selectedNotebookPageId = useSelector(
    (state: RootState) => state.pages.selectedNotebookPageId,
  );
  const fetchNotebookPage = useFetchNotebookPage(selectedNotebookPageId);
  const dispatch = useDispatch<AppDispatch>();
  const setSelectedNotebookPageId = (selectedNotebookPageId?: string) => {
    if (selectedNotebookPageId) {
      fetchNotebookPage();
    }
    const action: SetSelectedNotebookPageIdAction = {
      type: ActionType.SET_SELECTED_NOTEBOOK_PAGE_ID,
      pageId: selectedNotebookPageId,
    };
    dispatch(action as any);
  };
  return [selectedNotebookPageId, setSelectedNotebookPageId];
};

export const useSelectedNotebookPageIdMap = () => {
  const { selectedNotebookPageId, selectedNotebookPage } = useSelector(
    selectNotebookPageIdMap,
  );
  const dispatch = useDispatch<AppDispatch>();
  const setSelectedNotebookPageId = (selectedNotebookPageId?: string) => {
    const action: SetSelectedNotebookPageIdAction = {
      type: ActionType.SET_SELECTED_NOTEBOOK_PAGE_ID,
      pageId: selectedNotebookPageId,
    };
    dispatch(action as any);
  };
  return {
    selectedNotebookPageId,
    setSelectedNotebookPageId,
    selectedNotebookPage,
  };
};

export const useInsertHtmlBlock: () => (config: {
  blockIndex: number;
}) => void = () => {
  const insertBlock = useInsertBlock();
  return insertBlock;
};

export const useFlushNoteEdits: () => () => void = () => {
  const dispatch = useDispatch<AppDispatch>();
  return () => {
    const action: FlushNoteEditsAction = { type: ActionType.FLUSH_NOTE_EDITS };
    dispatch(action as any);
  };
};

export const useNoteEdits: () => [
  NoteEdits,
  (noteEdits: NoteEdits) => void,
] = () => {
  const noteEdits = useSelector(selectNoteEdits);
  const dispatch = useDispatch<AppDispatch>();
  const setNoteEdits = (noteEdits: NoteEdits) => {
    dispatch({ type: ActionType.SET_NOTE_EDITS, noteEdits });
  };
  return [noteEdits, setNoteEdits];
};

export const usePageBlocks = (pageId: string | undefined) => {
  const selector = useMemo(() => makeSelectPageBlocks(pageId), [pageId]);
  const pageBlocks = useSelector(selector);
  const dispatch = useDispatch<AppDispatch>();
  const setPageBlocks = (blocks: NoteBlock[]) => {
    if (!pageId) return;
    const action: SetPageBlocksAction = {
      type: ActionType.SET_PAGE_BLOCKS,
      pageId,
      blocks,
    };
    dispatch(action as any);
  };
  return { pageBlocks, setPageBlocks };
};

// The blocks of one page with an honest status: loading until SET_PAGE_BLOCKS, ready (possibly empty) after, error when
// the fetch failed. fetchBlocks is also the Retry handler.
export const usePageBlocksLoad = (pageId: string | undefined) => {
  const { pageBlocks, setPageBlocks } = usePageBlocks(pageId);
  const trackLoad = useTrackedLoad();
  const key = pageId ? loadKey("page-blocks", pageId) : undefined;
  const error = useLoadError(key);
  const fetchBlocks = () => {
    if (!pageId || !key) return Promise.resolve(undefined);
    return trackLoad(
      key,
      getPageBlocks({ "page-id": pageId }).then((blocks) => {
        setPageBlocks(blocks);
        return blocks;
      }),
    );
  };
  return { pageBlocks, status: loadStatus(pageBlocks, error), fetchBlocks };
};

export const useMoveNoteBlock: () => (config: {
  firstIndex: number;
  secondIndex: number;
}) => void = () => {
  const [appId] = useAppId();
  const dispatch = useDispatch<AppDispatch>();
  const { selectedNotebookPage, selectedNotebookPageId } =
    useSelectedNotebookPageIdMap();
  const { pageBlocks } = usePageBlocks(selectedNotebookPageId);

  const moveBlock = (config: { firstIndex: number; secondIndex: number }) => {
    const { firstIndex, secondIndex } = config;
    if (!appId || !selectedNotebookPageId || !pageBlocks) return;
    const blocks = pageBlocks;
    const block = blocks[firstIndex]!;
    const action: MoveNoteBlockAction = {
      type: ActionType.MOVE_NOTE_BLOCK,
      firstIndex,
      secondIndex,
    };
    const backendIndexParam =
      secondIndex > firstIndex ? secondIndex + 1 : secondIndex;
    // console.log("MOVE_BLOCK_INDEX", {
    //   firstIndex,
    //   secondIndex,
    //   backendIndexParam,
    // });
    dispatch(action as any);
    moveBlockIndex({
      "page-id": selectedNotebookPageId,
      "block-index": backendIndexParam,
      "app-id": appId,
      "block-id": block["block-id"],
    });
  };

  return moveBlock;
};

export const useFetchNotebookPage = (pageId) => {
  const [appId] = useAppId();
  const addNotebookPages = useAddNotebookPagesToState();
  const getNotebookPage = useGetNotebookPage();
  const { fetchBlocks } = usePageBlocksLoad(pageId);
  const trackLoad = useTrackedLoad();

  return async () => {
    if (!pageId) return;
    if (!appId) {
      throw new Error("appId not defined");
    }
    fetchBlocks();
    const page = await trackLoad(
      loadKey("page", pageId),
      getPage({ pageId }).then((page) => {
        startTransition(() => {
          addNotebookPages([page]);
        });
        return page;
      }),
    );
    return page;
  };
};

// Fetch multiple sub-page metadata entries in parallel and dispatch a single
// ADD_NOTEBOOK_PAGES action, avoiding N separate reducer runs when a page
// contains many sub-page blocks.
export const useBatchFetchSubPages = (subPageIds: string[]) => {
  const [appId] = useAppId();
  const addNotebookPages = useAddNotebookPagesToState();
  const store = useStore<RootState>();

  return useCallback(async () => {
    if (!appId || subPageIds.length === 0) return;
    // Read current state at fetch time — no subscription needed
    const notebookPages = selectNotebookPages(store.getState());
    const missing = subPageIds.filter((id) => !(id in notebookPages));
    if (missing.length === 0) return;
    const pages = await Promise.all(missing.map((id) => getPage({ pageId: id })));
    addNotebookPages(pages);
  }, [appId, subPageIds.join(",")]);
};

// Fetch all page-block sub-page content entries in parallel, dispatching
// SET_PAGE_BLOCKS individually as each fetch resolves for progressive rendering.
export const useBatchFetchPageBlocks = (pageIds: string[]): (() => Promise<void>) => {
  const [appId] = useAppId();
  const dispatch = useDispatch<AppDispatch>();
  return useCallback(async () => {
    if (!appId || pageIds.length === 0) return;
    await Promise.all(
      pageIds.map((pageId) =>
        getPageBlocks({ "page-id": pageId }).then((blocks) => {
          startTransition(() => {
            dispatch({ type: ActionType.SET_PAGE_BLOCKS, pageId, blocks } as any);
          });
        })
      )
    );
  }, [appId, pageIds.join(",")]);
};

export const useFetchNotebookPageListener: () => void = () => {
  const { selectedNotebookPageId, selectedNotebookPage } =
    useSelectedNotebookPageIdMap();
  const fetchNotebookPage = useFetchNotebookPage(selectedNotebookPageId);
  const { appId } = useAppIdMap();
  const { pageBlocks, fetchBlocks } = usePageBlocksLoad(selectedNotebookPageId);

  useEffect(() => {
    if (pageBlocks !== undefined) return;
    if (!selectedNotebookPageId) return;
    fetchBlocks();
  }, [selectedNotebookPageId, appId]);

  useEffect(() => {
    if (!selectedNotebookPageId || !appId || selectedNotebookPage) return;
    fetchNotebookPage();
  }, [selectedNotebookPageId, appId]);
};

export const useNotebookPageRouteParamListener: () => void = () => {
  const { "page-id": pageIdParam } = useParams();
  const { setSelectedNotebookPageId, selectedNotebookPageId: pageIdState } =
    useSelectedNotebookPageIdMap();
  useEffect(() => {
    if (pageIdParam !== pageIdState) {
      setSelectedNotebookPageId(pageIdParam);
    }
  }, [pageIdParam]);
};

export const usePageNameEditor = (pageId: string | undefined) => {
  const [localName, setLocalName] = useState<string | undefined>(undefined);
  const { page } = useNotebookPage(pageId);
  const pageNameState = page?.["page-data"].name;
  const setNoteName = useSetNoteName(pageId);

  const writeLocalToState = useMemo(() => debounce(setNoteName, 200), [pageId]);

  const setPageName = (name: string) => {
    setLocalName(name);
    writeLocalToState(name);
  };

  useEffect(() => {
    if (pageNameState === undefined) return;
    setLocalName(pageNameState);
  }, [pageNameState]);

  return { setPageName, pageName: localName ?? "" };
};

export const useSetNoteName: (
  pageId: string | undefined,
) => (name: string) => void = (pageId) => {
  const dispatch = useDispatch<AppDispatch>();
  const [appId] = useAppId();
  const { page } = useNotebookPage(pageId);
  const folderId = page?.["folder-id"];

  const writeBackendCallback = useMemo(
    () =>
      debounce(
        (config: {
          name: string;
          appId: string;
          folderId: string;
          pageId: string;
        }) => setPageName(config),
        500,
      ),
    [pageId],
  );

  return (name) => {
    if (!pageId || !folderId || !appId) return;
    writeBackendCallback({ appId, pageId, folderId, name });
    const action: SetPageNameAction = {
      type: ActionType.SET_PAGE_NAME,
      pageId,
      folderId,
      name,
    };
    dispatch(action as any);
  };
};

export const useFetchNotebook: () => () => Promise<Notebook> = () => {
  const [appId, setAppId] = useAppId();
  const [notebook, setNotebook] = useNotebook();
  const addPages = useAddNotebookPagesToState();

  return async () => {
    if (!appId) {
      throw new Error("app id is not defined");
    }

    return getPages({ "app-id": appId, "no-page-data": true }).then(
      (notebook) => {
        addPages(notebook.pages);
        setNotebook(notebook);
        return notebook;
      },
    );
  };
};

export const useBlockToFocus: () => [
  number | undefined,
  (blockToFocus: number | undefined) => void,
] = () => {
  const blockToFocus = useSelector(
    (state: RootState) => state.pages.blockToFocus,
  );
  const dispatch = useDispatch<AppDispatch>();

  const setBlockToFocus = (blockToFocus?: number) => {
    const action: SetBlockToFocusAction = {
      type: ActionType.SET_BLOCK_TO_FOCUS,
      blockToFocus,
    };
    dispatch(action as any);
  };
  return [blockToFocus, setBlockToFocus];
};

export const useSetNoteEdit: (
  index: number,
  debounced?: boolean,
) => (edit: string) => void = (index, debounced = true) => {
  const { selectedNotebookPageId, selectedNotebookPage } =
    useSelectedNotebookPageIdMap();
  const { pageBlocks, setPageBlocks } = usePageBlocks(selectedNotebookPageId);
  const pageBlock = pageBlocks ? pageBlocks[index] : undefined;

  const triggerDispatch = (edit: string) => {
    const block = pageBlocks![index] as HtmlBlock;
    const newBlock: HtmlBlock = { ...block, html: edit };
    const newBlocks: NoteBlock[] = R.update(index, newBlock, pageBlocks!);
    setPageBlocks(newBlocks);
    writeData(newBlock);
  };

  const triggerDispatchDebounced = debounced
    ? debounce(triggerDispatch, 300)
    : triggerDispatch;
  const callbackDispatch = useCallback(triggerDispatchDebounced, [pageBlock]);

  return callbackDispatch;
};

export const useDeleteTablesListener: () => void = () => {
  const deleteTables = useSelector(
    (state: RootState) => state.pages.deleteTables,
  );
  const dispatch = useDispatch<AppDispatch>();
  useEffect(() => {
    if (!deleteTables) return;
    deleteTables.map((tableId) => deleteTable({ tableId }));
    const action: SetDeleteTablesAction = {
      type: ActionType.SET_DELETE_TABLES,
      deleteTables: undefined,
    };
    dispatch(action as any);
  }, [deleteTables]);
};

export const useDatabasePages: (folderId: string) => NotebookPage[] = (
  folderId: string,
) => {
  const selector = useMemo(() => makeSelectDatabasePages(folderId), [folderId]);
  const pages = useSelector(selector);
  return pages;
};

export const useRootFolderId: () => string | undefined = () => {
  const [notebook] = useNotebook();
  const page = R.head(notebook?.pages ?? []);
  return page?.["folder-id"];
};

export const usePageBackPath: () => (
  pageId: string | undefined,
) => string = () => {
  const getPage = useGetNotebookPage();
  const pagePath = useNotebookPagePath();
  const rootFolderId = useRootFolderId();
  const [appId] = useAppId();

  return (pageId) => {
    const page = getPage(pageId);
    const parentId = page?.["folder-id"];

    return rootFolderId === parentId
      ? `/app/${appId}/notebook`
      : pagePath({ pageId: parentId });
  };
};

export const useFetchNotebookPageIsPublishedListener = () => {
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  const { setNotebookPageIsPublished, notebookPageIsPublished } =
    useNotebookPageIsPublished(selectedNotebookPageId);
  const { appId } = useAppIdMap();
  useEffect(() => {
    if (!selectedNotebookPageId || notebookPageIsPublished !== undefined) {
      return;
    }
    getPageIsPublished({
      "app-id": appId!,
      "page-id": selectedNotebookPageId!,
    }).then((res) => {
      setNotebookPageIsPublished(res.published === "true" ? true : false);
    });
  }, [selectedNotebookPageId, notebookPageIsPublished]);
};

export const useNotebookPageIsPublished = (pageId: string | undefined) => {
  const dispatch = useDispatch<AppDispatch>();
  const [appId] = useAppId();
  const notebookPageIsPublished = useSelector((state: RootState) => {
    const publishedPagesMap = state.pages.publishedPagesMap;
    return pageId ? (publishedPagesMap[pageId] ?? undefined) : undefined;
  });
  const writeBackend = useCallback(
    debounce((notebookPageIsPublished: boolean) => {
      if (!appId) return;
      if (!pageId) return;
      setPageIsPublished({
        "app-id": appId,
        "page-id": pageId,
        published: notebookPageIsPublished ? "true" : "false",
      });
    }, 500),
    [appId, pageId],
  );
  const setNotebookPageIsPublishedBackend = (
    notebookPageIsPublished: boolean,
  ) => {
    setNotebookPageIsPublished(notebookPageIsPublished);
    writeBackend(notebookPageIsPublished);
  };
  const setNotebookPageIsPublished = (notebookPageIsPublished: boolean) => {
    if (!pageId) return;
    const action: SetPagePublishedAction = {
      type: ActionType.SET_PAGE_PUBLISHED,
      pageId,
      published: notebookPageIsPublished,
    };
    dispatch(action as any);
  };

  return {
    notebookPageIsPublished,
    setNotebookPageIsPublished,
    setNotebookPageIsPublishedBackend,
  };
};

export const useNotebookPageAccess = (pageId: string | undefined) => {
  const { notebookPageIsPublished } = useNotebookPageIsPublished(pageId);
  const { hasAccess: subscriptionAccess, loading: subscriptionLoading } = useSubscriptionStatus();
  const { userHasApp } = useUserHasApp();
  const appsLoaded = useSelector((state: RootState) => state.settings.appsLoaded);
  const appId = useSelector((state: RootState) => state.ui.appId);
  const writeAccess = userHasApp && subscriptionAccess;
  const hasAccess = notebookPageIsPublished || writeAccess;
  // loading must stay true until both apps and appId are resolved so that
  // the hasAccess guard doesn't fire before userHasApp can be computed
  const loading = subscriptionLoading || !appsLoaded || !appId;

  return { hasAccess, loading, writeAccess };
};

export const useSetNotePageTitleListener = () => {
  const { selectedNotebookPage, selectedNotebookPageId } =
    useSelectedNotebookPageIdMap();
  const pageName = selectedNotebookPage?.["page-data"].name;
  useEffect(() => {
    document.title = pageName ?? "";
  }, [pageName, selectedNotebookPageId]);
};

export const useMovePage = () => {
  const { appId } = useAppIdMap();
  return (config: {
    "source-page-id": string;
    "destination-page-id": string;
  }) => {
    return movePage({ ...config, "app-id": appId! });
  };
};

export const useIsNoteboookPageSelected = (pageId: string | undefined) => {
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  if (!selectedNotebookPageId) return false;
  if (!pageId) return false;
  return selectedNotebookPageId === pageId;
};
